import React, { ReactNode, useState } from "react";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Link, useNavigate } from "react-router-dom";
import { FaWhatsapp } from "react-icons/fa";
import { observer } from "mobx-react-lite";

import StatusBadge from "./StatusBadge";
import { useStore } from "../../app/stores/store";
import { separateCamelCase } from "../../lib/utils";
import { all_routes } from "../router/all_routes";
import { IMAGE_SERVER_PATH } from "../../environment";
import DateCalculator from "../clients/DateCalculator";

type FileResponseData = {
  fileName: string;
  filePath: string;
};

type ExtraColumn = {
  header: string;
  accessor: string;
  render: (value: any, rowData?: any) => React.ReactNode;
};

type Props = {
  data: any[];
  rowsPerPageOptions: number[];
  pageSize?: number;
  status?: ReactNode;
  exceptColumns: string[];
  extraColumns?: ExtraColumn[];
  setSelectedId?: (id: string) => void;
  routeUrl: string;
  dialogFlags: any;
  getViewId?: (id: string) => void;
  customCellRenderers?: {
    [key: string]: (value: any, rowData?: any) => React.ReactNode;
  };
};

const whatsappColumn: ExtraColumn = {
  header: "WhatsApp",
  accessor: "whatsAppLink",
  render: (value: string | null) =>
    value ? (
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#25D366", fontSize: "1.25rem" }}
        title="Open WhatsApp"
      >
        <FaWhatsapp />
      </a>
    ) : (
      <span className="text-gray-400 italic">No WhatsApp</span>
    ),
};

const headerMap: Record<string, string> = {
  name: "Name",
  passportNumber: "Passport No.",
  entryDateToUAE: "Entry Date",
  touristVisaIssueDate: "Visa Issue Date",
  remainingDaysToExit: "Days Left",
  allowedDuration: "Allowed Stay",
  

  // Add more headers if needed
};

const TableClient = ({
  data,
  rowsPerPageOptions,
  pageSize,
  status,
  exceptColumns,
  extraColumns = [whatsappColumn],
  setSelectedId,
  routeUrl,
  dialogFlags,
  getViewId,
}: Props) => {
  const {
    userStore: { isAdmin },
    clientStore,
  } = useStore();
  const navigate = useNavigate();

  const viewAction = (rowData: any) => {
    const { id } = rowData;
    return (
      <Link
        to="#"
        onClick={() => getViewId?.(id)}
        data-bs-toggle="modal"
        data-bs-target={`#${routeUrl}`}
      >
        <i className="feather icon-eye text-blue-500 hover:text-blue-700 cursor-pointer"></i>
      </Link>
    );
  };

  const renderCell = (key: string, rowData: any) => {
    const value = rowData[key];

    if (key === "remainingDaysToExit") {
      return <strong className="text-red-600">{value}</strong>;
    }

    if (typeof value === "object" && value !== null) {
      if ("filePath" in value && "fileName" in value) {
        return (
          <a href={value.filePath} target="_blank" rel="noopener noreferrer">
            {value.fileName}
          </a>
        );
      }
      return <span className="text-black-500 italic">[Object]</span>;
    }

    switch (key) {
      case "bookingCode":
        return (
          <Link
            to={`/listings/booking/view/${rowData.bookingId}`}
            className="text-red-500 font-bold"
            style={{ textDecoration: "underline" }}
          >
            {value}
          </Link>
        );
      case "customerName":
        return (
          <span
            onClick={() => getViewId?.(rowData.id)}
            className="text-black font-bold"
            style={{ cursor: "pointer" }}
          >
            {value}
          </span>
        );
      case "bookingStatus":
        return <StatusBadge status={value} />;
      case "enquiryType":
        return value ? "Business Setup" : "General Contact";
      case "email":
      case "nationalityName":
        return value || "No Data";
      case "isComplaint":
        return value ? "Complaint" : "Suggestion";
      case "isComing":
        return value ? "Yes" : "No";
      default:
        return value;
    }
  };

  // Check if 'followUpComments' is already included in extraColumns to avoid duplicate
  const hasFollowUpCommentsColumn = extraColumns.some(
    (col) => col.accessor === "followUpComments"
  );

  return (
<>


    
    <DataTable
      className="table datatable p-datatable-sm"
      value={data}
      rows={pageSize ?? 10}
      rowsPerPageOptions={rowsPerPageOptions}
      tableStyle={{ fontSize: "0.875rem" }}
    >
      {data &&
        (() => {
          const allKeys = Object.keys(data[0] || {}).filter(
            (key) => !exceptColumns.includes(key)
          );

          const sortedKeys = [
            "bookingCode",
            ...allKeys.filter((k) => k !== "bookingCode"),
          ];

          return sortedKeys.map((key) => (
            <Column
              key={key}
              field={key}
              header={headerMap[key] ?? separateCamelCase(key)}
              body={(rowData: any) => renderCell(key, rowData)}
              style={{ fontSize: "0.875rem", padding: "8px" }}
            />
          ));
        })()}

      {/* Render extraColumns */}
      {extraColumns?.map((col, idx) => (
        <Column
          key={col.accessor + idx}
          header={col.header}
          body={(rowData: any) => col.render(rowData[col.accessor], rowData)}
        />
      ))}

      {/* Conditionally render Follow-Up Comments column */}
      {!hasFollowUpCommentsColumn && (
        <Column
          field="followUpComments"
          header="Follow-Up Comments"
          body={(rowData) =>
            rowData.followUpComments ? (
            <ul
          className="text-sm text-gray-700"
  style={{ whiteSpace: "normal", maxWidth: "250px" }}
        >
          {rowData.followUpComments.split("\n").map(
            (comment: string, index: number) => (
              <li key={index}>
                <span className="text-gray-600">•</span> {comment}
              </li>
            )
          )}
        </ul>
            ) : (
              <span className="italic text-gray-400">No comments</span>
            )
          }
        />
      )}

      {/* Files column */}
      <Column
        key="fileEntities"
        field="fileEntities"
        header="Files"
        body={(rowData: any) =>
          rowData.fileEntities && rowData.fileEntities.length > 0 ? (
            <div className="flex gap-2 flex-wrap">
              {rowData.fileEntities.map(
                (file: FileResponseData, index: number) => {
                  const fileUrl = `${IMAGE_SERVER_PATH}/${file.filePath}`;
                  return (
                    <a
                      key={index}
                      href={fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      <i className="feather icon-download" />
                    </a>
                  );
                }
              )}
            </div>
          ) : (
            <span className="text-gray-400 italic">No files</span>
          )
        }
      />

      {/* Actions column */}
      <Column
        header="Actions"
        body={(rowData: any) =>
          rowData.fileEntities && rowData.fileEntities.length > 0 ? (
            <Link
              to="#"
              onClick={() => {
                setSelectedId?.(rowData.id);
                clientStore.getClientItem(rowData.id);
              }}
              data-bs-toggle="modal"
              data-bs-target={`#${dialogFlags.visaEditDialog}`}
            >
              <i className="feather icon-edit text-blue-500 hover:text-blue-700 cursor-pointer"></i>
            </Link>
          ) : (
            <span className="text-gray-400 italic">No Files</span>
          )
        }
      />

<Column
  header="Actions"
  body={(rowData: any) => {
    const isDisabled =
      rowData.remainingDaysToExit <= 0 || rowData.isActive === false;

    return (
      <button
        onClick={() => {
          if (!isDisabled) {
            setSelectedId?.(rowData.id);
            clientStore.getClientItem(rowData.id);
            navigate(`/listings/service-details/5db658b3-8195-4013-5e47-08ddda5df907`);
          }
        }}
        className={`btn btn-sm ${isDisabled ? "btn-secondary disabled" : "btn-primary"}`}
        disabled={isDisabled}
      >
        Extend
      </button>
    );
  }}
/>



      {/* Optional Status column */}
      {status && <Column field="status" header="Status" body={status} />}

      {/* Conditionally render dashboard-specific columns */}
      {(routeUrl === all_routes.categoryDashboard ||
        routeUrl === all_routes.serviceDashboard ||
        routeUrl === all_routes.lostDashboard ||
        routeUrl === all_routes.ComplaintDashboard) && (
        <Column header="View" body={viewAction} />
      )}

      {(routeUrl === all_routes.lostDashboard ||
        routeUrl === all_routes.ComplaintDashboard) && (
        <>
          <Column header="Complete" body={viewAction} />
          <Column header="In Process" body={viewAction} />
        </>
      )}

      {routeUrl === all_routes.notificationDashboard && isAdmin() && (
        <Column
          header="Booking Details"
          body={(rowData: any) => (
            <Link to={`/listings/${rowData.moreDetailsUrl}`}>
              <i className="feather icon-loader text-orange-500 font-bold me-1"></i>
            </Link>
          )}
        />
      )}
    </DataTable>

    </>
  );
};

export default observer(TableClient);
